import { useState, useEffect, useCallback } from 'react';

export const useMatchManagement = (updateRealResults, setIsLoading, setSuccess, setError) => {
  // Real results state
  const [realResults, setRealResults] = useState({
    leagueStanding: [],
    playoffResults: [],
    roundOf16Results: [],
    quarterFinalResults: [],
    semiFinalResults: [],
    finalResult: {}
  });

  // Matches data
  const [matches, setMatches] = useState({
    playoff: Array.from({ length: 8 }, (_, i) => ({
      id: `match-${i + 1}`,
      home: '',
      away: '',
      winner: '',
      homeScore: 0,
      awayScore: 0
    })),
    roundOf16: Array.from({ length: 8 }, (_, i) => ({
      id: `match-${i + 1}`,
      home: '',
      away: '',
      winner: '',
      homeScore: 0,
      awayScore: 0,
      aggregateScore: ''
    })),
    quarterFinals: Array.from({ length: 4 }, (_, i) => ({
      id: `match-${i + 1}`,
      home: '',
      away: '',
      winner: '',
      homeScore: 0,
      awayScore: 0,
      aggregateScore: ''
    })),
    semiFinals: Array.from({ length: 2 }, (_, i) => ({
      id: `match-${i + 1}`,
      home: '',
      away: '',
      winner: '',
      homeScore: 0,
      awayScore: 0,
      aggregateScore: ''
    })),
    final: Array.from({ length: 1 }, (_, i) => ({
      id: `match-${i + 1}`,
      home: '',
      away: '',
      winner: '',
      homeScore: 0,
      awayScore: 0,
      aggregateScore: ''
    }))
  });

  // Track pending changes
  const [pendingChanges, setPendingChanges] = useState(false);

  // API key for external data
  const [apiKey, setApiKey] = useState('');

  // Helper function to map roundType to backend field names
  const mapRoundTypeToBackendField = (roundType) => {
    const mapping = {
      'playoff': 'playoffResults',
      'roundOf16': 'roundOf16Results',
      'quarterFinals': 'quarterFinalResults',
      'semiFinals': 'semiFinalResults',
      'final': 'finalResult'
    };
    return mapping[roundType] || roundType;
  };

  // Helper function to convert matches to realResults format
  const convertMatchesToRealResults = (matchesData) => {
    if (!matchesData) {
      return {
        leagueStanding: realResults.leagueStanding || [],
        playoffResults: [],
        roundOf16Results: [],
        quarterFinalResults: [],
        semiFinalResults: [],
        finalResult: {}
      };
    }

    const converted = {
      leagueStanding: realResults.leagueStanding || [],
      playoffResults: (matchesData.playoff && Array.isArray(matchesData.playoff))
        ? matchesData.playoff
            .filter(match => match && match.home && match.away)
            .map(match => ({
              id: match.id,
              home: match.home,
              away: match.away,
              winner: match.winner || '',
              homeScore: match.homeScore ?? 0,
              awayScore: match.awayScore ?? 0
            }))
        : [],
      roundOf16Results: (matchesData.roundOf16 && Array.isArray(matchesData.roundOf16))
        ? matchesData.roundOf16
            .filter(match => match && match.id)
            .map(match => ({
              id: match.id,
              home: match.home || '',
              away: match.away || '',
              winner: match.winner || '',
              homeScore: match.homeScore ?? 0,
              awayScore: match.awayScore ?? 0,
              aggregateScore: match.aggregateScore || ''
            }))
        : [],
      quarterFinalResults: (matchesData.quarterFinals && Array.isArray(matchesData.quarterFinals))
        ? matchesData.quarterFinals
            .filter(match => match && match.id)
            .map(match => ({
              id: match.id,
              home: match.home || '',
              away: match.away || '',
              winner: match.winner || '',
              homeScore: match.homeScore ?? 0,
              awayScore: match.awayScore ?? 0,
              aggregateScore: match.aggregateScore || ''
            }))
        : [],
      semiFinalResults: (matchesData.semiFinals && Array.isArray(matchesData.semiFinals))
        ? matchesData.semiFinals
            .filter(match => match && match.id)
            .map(match => ({
              id: match.id,
              home: match.home || '',
              away: match.away || '',
              winner: match.winner || '',
              homeScore: match.homeScore ?? 0,
              awayScore: match.awayScore ?? 0,
              aggregateScore: match.aggregateScore || ''
            }))
        : [],
      finalResult: (matchesData.final && Array.isArray(matchesData.final) && matchesData.final[0] && matchesData.final[0].home && matchesData.final[0].away)
        ? {
            id: matchesData.final[0].id,
            home: matchesData.final[0].home,
            away: matchesData.final[0].away,
            winner: matchesData.final[0].winner || '',
            homeScore: matchesData.final[0].homeScore ?? 0,
            awayScore: matchesData.final[0].awayScore ?? 0,
            aggregateScore: matchesData.final[0].aggregateScore || ''
          }
        : {}
    };
    return converted;
  };

  // Helper function to convert realResults to matches format
  const convertRealResultsToMatches = (realResultsData) => {
    if (!realResultsData || typeof realResultsData !== 'object') {
      // Return default empty structure if realResultsData is invalid
      return {
        playoff: Array.from({ length: 8 }, (_, i) => ({
          id: `match-${i + 1}`,
          home: '',
          away: '',
          winner: '',
          homeScore: 0,
          awayScore: 0
        })),
        roundOf16: Array.from({ length: 8 }, (_, i) => ({
          id: `match-${i + 1}`,
          home: '',
          away: '',
          winner: '',
          homeScore: 0,
          awayScore: 0,
          aggregateScore: ''
        })),
        quarterFinals: Array.from({ length: 4 }, (_, i) => ({
          id: `match-${i + 1}`,
          home: '',
          away: '',
          winner: '',
          homeScore: 0,
          awayScore: 0,
          aggregateScore: ''
        })),
        semiFinals: Array.from({ length: 2 }, (_, i) => ({
          id: `match-${i + 1}`,
          home: '',
          away: '',
          winner: '',
          homeScore: 0,
          awayScore: 0,
          aggregateScore: ''
        })),
        final: Array.from({ length: 1 }, (_, i) => ({
          id: `match-${i + 1}`,
          home: '',
          away: '',
          winner: '',
          homeScore: 0,
          awayScore: 0,
          aggregateScore: ''
        }))
      };
    }

    // Helper function to normalize match ID to match-{number} format
    const normalizeMatchId = (id, index) => {
      if (!id) return `match-${index + 1}`;
      // Extract number from ID (e.g., 'playoff_1' -> '1', 'match-1' -> '1')
      const match = id.match(/(\d+)/);
      if (match) {
        return `match-${match[1]}`;
      }
      return `match-${index + 1}`;
    };

    // Helper to ensure we always have the correct number of matches
    const ensureMatchArray = (matchesArray, expectedLength, roundType) => {
      const result = Array.from({ length: expectedLength }, (_, index) => {
        const matchId = `match-${index + 1}`;
        // Try to find existing match by position
        const existingMatch = matchesArray && matchesArray.find(match => {
          if (!match || !match.id) return false;
          const matchNumber = match.id.match(/(\d+)/);
          return matchNumber && parseInt(matchNumber[1]) === index + 1;
        });
        
        return existingMatch ? {
          id: matchId,
          home: existingMatch.home || '',
          away: existingMatch.away || '',
          winner: existingMatch.winner || '',
          homeScore: existingMatch.homeScore || 0,
          awayScore: existingMatch.awayScore || 0,
          aggregateScore: existingMatch.aggregateScore || ''
        } : {
          id: matchId,
          home: '',
          away: '',
          winner: '',
          homeScore: 0,
          awayScore: 0,
          aggregateScore: ''
        };
      });
      return result;
    };

    const converted = {
      playoff: ensureMatchArray(realResultsData.playoffResults, 8, 'playoff'),
      roundOf16: ensureMatchArray(realResultsData.roundOf16Results, 8, 'roundOf16'),
      quarterFinals: ensureMatchArray(realResultsData.quarterFinalResults, 4, 'quarterFinals'),
      semiFinals: ensureMatchArray(realResultsData.semiFinalResults, 2, 'semiFinals'),
      final: realResultsData.finalResult && realResultsData.finalResult.home && realResultsData.finalResult.away
        ? [{
            id: normalizeMatchId(realResultsData.finalResult.id, 0),
            home: realResultsData.finalResult.home || '',
            away: realResultsData.finalResult.away || '',
            winner: realResultsData.finalResult.winner || '',
            homeScore: realResultsData.finalResult.homeScore || 0,
            awayScore: realResultsData.finalResult.awayScore || 0,
            aggregateScore: realResultsData.finalResult.aggregateScore || ''
          }]
        : Array.from({ length: 1 }, (_, i) => ({
            id: `match-${i + 1}`,
            home: '',
            away: '',
            winner: '',
            homeScore: 0,
            awayScore: 0,
            aggregateScore: ''
          }))
    };
    return converted;
  };

  // Load data from localStorage on mount
  useEffect(() => {
    const savedResults = localStorage.getItem('championsLeagueRealResults');
    if (savedResults) {
      const parsed = JSON.parse(savedResults);
      setRealResults(parsed);
      // Also update matches state from realResults
      // Note: convertRealResultsToMatches is defined above, so we can use it directly here
      const convertedMatches = convertRealResultsToMatches(parsed);
      setMatches(convertedMatches);
    } else {
      // If no saved results, try to load matches directly
      const savedMatches = localStorage.getItem('championsLeagueMatches');
      if (savedMatches) {
        setMatches(JSON.parse(savedMatches));
      }
    }

    const savedApiKey = localStorage.getItem('apiFootballKey');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    }
  }, []);

  // Helper function to find changed matches by comparing with existing results
  const findChangedMatches = useCallback((currentMatches, existingResults, roundType) => {
    const existingMatches = existingResults || [];
    const changedMatches = [];
    
    if (!currentMatches || !Array.isArray(currentMatches)) {
      return changedMatches;
    }
    
    currentMatches.forEach(currentMatch => {
      if (!currentMatch || !currentMatch.id) return;
      
      // Find existing match with same ID
      const existingMatch = existingMatches.find(m => {
        if (!m || !m.id) return false;
        const currentNumber = currentMatch.id.match(/(\d+)/);
        const existingNumber = m.id.match(/(\d+)/);
        return currentNumber && existingNumber && currentNumber[1] === existingNumber[1];
      });
      
      // Check if match has changed
      if (!existingMatch) {
        // New match - include if it has any data (inkl. kun mål uden holdnavne endnu)
        const hasScores =
          (currentMatch.homeScore ?? 0) !== 0 || (currentMatch.awayScore ?? 0) !== 0;
        if (
          currentMatch.winner ||
          currentMatch.home ||
          currentMatch.away ||
          hasScores ||
          (currentMatch.aggregateScore && String(currentMatch.aggregateScore).trim() !== '')
        ) {
          changedMatches.push(currentMatch);
        }
      } else {
        // Sammenlign med normaliserede tal (GraphQL kan give strenger fra cache)
        const scoreEq = (a, b) => Number(a ?? 0) === Number(b ?? 0);
        const hasChanged =
          currentMatch.winner !== (existingMatch.winner || '') ||
          currentMatch.home !== (existingMatch.home || '') ||
          currentMatch.away !== (existingMatch.away || '') ||
          !scoreEq(currentMatch.homeScore, existingMatch.homeScore) ||
          !scoreEq(currentMatch.awayScore, existingMatch.awayScore) ||
          currentMatch.aggregateScore !== (existingMatch.aggregateScore || '');

        if (hasChanged) {
          changedMatches.push(currentMatch);
        }
      }
    });
    
    return changedMatches;
  }, []);

  const saveResults = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Ensure matches is properly initialized
      if (!matches || typeof matches !== 'object') {
        console.error('Matches state is invalid:', matches);
        setError('Fejl: Matches data er ikke korrekt initialiseret');
        return;
      }
      
      // Find only changed matches
      const changedRoundOf16 = findChangedMatches(matches.roundOf16, realResults.roundOf16Results, 'roundOf16');
      const changedQuarterFinals = findChangedMatches(matches.quarterFinals, realResults.quarterFinalResults, 'quarterFinals');
      const changedSemiFinals = findChangedMatches(matches.semiFinals, realResults.semiFinalResults, 'semiFinals');
      const changedPlayoff = findChangedMatches(matches.playoff, realResults.playoffResults, 'playoff');
      
      // Convert changed matches to realResults format
      const changedResults = {
        // Only include leagueStanding if it's actually being changed (not in this case)
        // leagueStanding: realResults.leagueStanding || [],
        playoffResults: changedPlayoff.length > 0 ? changedPlayoff.map(match => ({
          id: match.id,
          home: match.home || '',
          away: match.away || '',
          winner: match.winner || '',
          homeScore: match.homeScore ?? 0,
          awayScore: match.awayScore ?? 0
        })) : undefined,
        roundOf16Results: changedRoundOf16.length > 0 ? changedRoundOf16.map(match => ({
          id: match.id,
          home: match.home || '',
          away: match.away || '',
          winner: match.winner || '',
          homeScore: match.homeScore ?? 0,
          awayScore: match.awayScore ?? 0,
          aggregateScore: match.aggregateScore || ''
        })) : undefined,
        quarterFinalResults: changedQuarterFinals.length > 0 ? changedQuarterFinals.map(match => ({
          id: match.id,
          home: match.home || '',
          away: match.away || '',
          winner: match.winner || '',
          homeScore: match.homeScore ?? 0,
          awayScore: match.awayScore ?? 0,
          aggregateScore: match.aggregateScore || ''
        })) : undefined,
        semiFinalResults: changedSemiFinals.length > 0 ? changedSemiFinals.map(match => ({
          id: match.id,
          home: match.home || '',
          away: match.away || '',
          winner: match.winner || '',
          homeScore: match.homeScore ?? 0,
          awayScore: match.awayScore ?? 0,
          aggregateScore: match.aggregateScore || ''
        })) : undefined,
        // Gem finalepar når både home og away er valgt — winner kan først sættes efter kampen
        finalResult: matches.final && matches.final[0] && matches.final[0].home && matches.final[0].away ? {
          id: matches.final[0].id,
          home: matches.final[0].home || '',
          away: matches.final[0].away || '',
          winner: matches.final[0].winner || '',
          homeScore: matches.final[0].homeScore ?? 0,
          awayScore: matches.final[0].awayScore ?? 0,
          aggregateScore: matches.final[0].aggregateScore || ''
        } : undefined
      };

      // Update real results in backend (only changed matches)
      const response = await updateRealResults({
        variables: {
          realResults: changedResults
        }
      });

      // Update state immediately from response (before refetch completes)
      if (response?.data?.updateChampionsLeagueRealResults?.realResults) {
        const updatedRealResults = response.data.updateChampionsLeagueRealResults.realResults;

        // Update realResults state
        setRealResults(updatedRealResults);
        
        // Also update matches state
        const convertedMatches = convertRealResultsToMatches(updatedRealResults);

        // Force React to re-render by creating new object references
        // Create a completely new object to ensure React detects the change
        const newMatchesState = {
          playoff: [...(convertedMatches.playoff || [])],
          roundOf16: [...(convertedMatches.roundOf16 || [])],
          quarterFinals: [...(convertedMatches.quarterFinals || [])],
          semiFinals: [...(convertedMatches.semiFinals || [])],
          final: [...(convertedMatches.final || [])]
        };
        setMatches(newMatchesState);
        
        // Save to localStorage as backup
        localStorage.setItem('championsLeagueRealResults', JSON.stringify(updatedRealResults));
        localStorage.setItem('championsLeagueMatches', JSON.stringify(convertedMatches));
      } else {
        console.warn('[CL Admin] Gem — intet realResults i GraphQL-svar (tjek netværk / fejl i mutation):', response);
      }
      
      setPendingChanges(false);
      setSuccess('Resultater gemt til backend og lokalt!');
    } catch (error) {
      setError(`Fejl ved gemning: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    matches,
    realResults,
    findChangedMatches,
    updateRealResults,
    setIsLoading,
    setSuccess,
    setError,
  ]);

  // Handle match changes
  const handleMatchChange = useCallback((roundType, matchId, field, value) => {
    setMatches(prev => {
      // Ensure the roundType array exists
      if (!prev[roundType]) {
        prev[roundType] = [];
      }
      
      // Find if match already exists - check both exact ID match and position-based match
      const existingMatchIndex = prev[roundType].findIndex(match => {
        if (!match || !match.id) return false;
        // Exact ID match
        if (match.id === matchId) return true;
        // Position-based match (extract number from both IDs)
        const matchNumber = match.id.match(/(\d+)/);
        const targetNumber = matchId.match(/(\d+)/);
        if (matchNumber && targetNumber && matchNumber[1] === targetNumber[1]) return true;
        return false;
      });
      
      let updatedRoundMatches;
      if (existingMatchIndex >= 0) {
        // Update existing match - preserve all fields
        updatedRoundMatches = prev[roundType].map(match => {
          const isMatch = match.id === matchId || 
            (match.id && matchId && 
             match.id.match(/(\d+)/)?.[1] === matchId.match(/(\d+)/)?.[1]);
          return isMatch
            ? { ...match, [field]: value }
            : match;
        });
      } else {
        // Create new match if it doesn't exist - ensure it has the correct ID format
        const newMatch = {
          id: matchId,
          home: '',
          away: '',
          winner: '',
          homeScore: 0,
          awayScore: 0,
          aggregateScore: ''
        };
        newMatch[field] = value;
        
        // Ensure we maintain the correct number of matches for this round
        // Find the position index from matchId
        const matchNumber = matchId.match(/(\d+)/);
        const positionIndex = matchNumber ? parseInt(matchNumber[1]) - 1 : prev[roundType].length;
        
        // Create array with correct length and insert match at correct position
        const maxLength = Math.max(prev[roundType].length, positionIndex + 1);
        updatedRoundMatches = Array.from({ length: maxLength }, (_, index) => {
          if (index === positionIndex) {
            return newMatch;
          }
          // Keep existing match if it exists, otherwise create placeholder
          return prev[roundType][index] || {
            id: `match-${index + 1}`,
            home: '',
            away: '',
            winner: '',
            homeScore: 0,
            awayScore: 0,
            aggregateScore: ''
          };
        });
      }
      
      const updated = {
        ...prev,
        [roundType]: updatedRoundMatches
      };
      
      // Mark as pending changes
      setPendingChanges(true);
      
      return updated;
    });
  }, []);

  // Handle playoff result changes
  const handlePlayoffResultChange = useCallback((matchId, winner) => {
    setRealResults(prev => ({
      ...prev,
      playoffResults: winner
    }));
  }, []);

  // Handle knockout result changes
  const handleKnockoutResultChange = useCallback((round, matchId, winner) => {
    setRealResults(prev => ({
      ...prev,
      [round]: prev[round] ? prev[round].map(match => 
        match.id === matchId ? { ...match, winner } : match
      ) : []
    }));
  }, []);

  // Handle final winner change
  const handleFinalWinnerChange = useCallback((winner) => {
    setRealResults(prev => ({
      ...prev,
      finalResult: {
        id: 'final',
        home: '',
        away: '',
        winner: winner,
        homeScore: 0,
        awayScore: 0,
        aggregateScore: ''
      }
    }));
  }, []);

  // API Football integration
  const saveApiKey = useCallback(() => {
    localStorage.setItem('apiFootballKey', apiKey);
    setSuccess('API nøgle gemt!');
  }, [apiKey, setSuccess]);

  const fetchFromAPI = useCallback(async () => {
    if (!apiKey.trim()) {
      setError('Indtast venligst en API nøgle først');
      return;
    }

    setIsLoading(true);
    try {
      // Mock API call - replace with real API-Football integration
      const response = await fetch('https://api-football-v1.p.rapidapi.com/v3/standings?league=2&season=2024', {
        method: 'GET',
        headers: {
          'X-RapidAPI-Key': apiKey,
          'X-RapidAPI-Host': 'api-football-v1.p.rapidapi.com'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      // Process API data and update team points
      // This would need to be implemented based on actual API response structure
      
      setSuccess('Data hentet fra API!');
    } catch (error) {
      console.error('API Error:', error);
      setError(`Fejl ved hentning fra API: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, setIsLoading, setSuccess, setError]);

  // Memoize convertRealResultsToMatches to avoid recreating it on every render
  const convertRealResultsToMatchesMemo = useCallback((realResultsData) => {
    return convertRealResultsToMatches(realResultsData);
  }, []);

  // Custom setRealResults that also updates matches
  const setRealResultsWithMatches = useCallback((newRealResults) => {
    // Only update if data has actually changed to avoid infinite loops
    if (!newRealResults || typeof newRealResults !== 'object') {
      return;
    }
    
    // Quick reference check first (same object reference)
    if (realResults === newRealResults) {
      return;
    }
    
    // Deep comparison to avoid unnecessary updates
    const currentStr = JSON.stringify(realResults);
    const newStr = JSON.stringify(newRealResults);
    if (currentStr === newStr) {
      return;
    }
    
    setRealResults(newRealResults);
    // Also update matches when realResults is set externally
    const convertedMatches = convertRealResultsToMatchesMemo(newRealResults);
    
    // Ensure converted matches has correct structure
    if (convertedMatches && typeof convertedMatches === 'object') {
      setMatches(convertedMatches);
    }
    setPendingChanges(false); // Reset pending changes when loading from backend
  }, [realResults, convertRealResultsToMatchesMemo]);

  return {
    // State
    realResults,
    setRealResults: setRealResultsWithMatches,
    matches,
    setMatches,
    apiKey,
    setApiKey,
    pendingChanges,
    
    // Handlers
    saveResults,
    handleMatchChange,
    handlePlayoffResultChange,
    handleKnockoutResultChange,
    handleFinalWinnerChange,
    saveApiKey,
    fetchFromAPI
  };
};
